// ============================================
// 敵の設定（ENEMY_CONFIG）
// ============================================


export interface BaseEnemyConfig {
    size: number;
    hp: number;
    speedMult: number;
    score: number;
    spawnWeight: number;
    minWave: number;
    color: readonly [number, number, number];
    rotSpeed: number;
}

export interface ArmoredEnemyConfig extends BaseEnemyConfig {
    armorChance: number;
    dropBonus: number;
}

export interface SpeedyEnemyConfig extends BaseEnemyConfig {
    zigzagInterval: number;
}

export interface LaserEnemyConfig extends BaseEnemyConfig {
    stopY: number;
    chargeTime: number;
    laserDuration: number;
    laserWidth: number;
    warningTime: number;
}

export interface MirrorEnemyConfig extends BaseEnemyConfig {
    laneChangeDelay: number;
}

export interface BossAttackConfig {
    straightInterval: number;
    spreadInterval: number;
    spreadCount: number;
    homingInterval: number;
    barrageInterval: number;
    barrageCount: number;
}

export interface BossEnemyConfig {
    // --- 基本ステータス ---
    size: number;
    baseHp: number;
    hpPerWave: number;
    speedMult: number;
    score: number;
    color: readonly [number, number, number];
    innerColor: readonly [number, number, number];
    rotSpeed: number;
    laneChangeInterval: number;
    // --- 出現設定 ---
    spawnWave: number;
    warningDuration: number;
    defeatPauseDuration: number;
    // --- 滞在フェーズ設定 ---
    stayDuration: number;
    stayPositionY: number;
    // --- 下降フェーズ設定 ---
    descentSpeed: number;
    descentDistance: number;
    descentPauseDuration: number;
    zigzagAmplitude: number;
    zigzagSpeed: number;
    playerTrackRate: number;
    // --- 攻撃パターン ---
    attack: BossAttackConfig;
}

type EnemyConfigMap = {
    normal: BaseEnemyConfig;
    tank: BaseEnemyConfig;
    fast: BaseEnemyConfig;
    armored: ArmoredEnemyConfig;
    speedy: SpeedyEnemyConfig;
    laser: LaserEnemyConfig;
    mirror: MirrorEnemyConfig;
    boss: BossEnemyConfig;
};

export const ENEMY_CONFIG: EnemyConfigMap = {
    normal: {
        size: 32,            // サイズ（少し小さく）
        hp: 100,             // HP
        speedMult: 1.0,      // 速度倍率
        score: 100,          // 撃破時のスコア
        spawnWeight: 45,     // 出現確率の重み（%）
        minWave: 1,          // このウェーブ未満では出現しない（汎用アンロック条件）
        color: [255, 50, 100], // 色 [R, G, B]
        rotSpeed: 0.05       // 回転速度
    },
    tank: {
        size: 42,
        hp: 300,             // HP
        speedMult: 0.75,
        score: 180,
        spawnWeight: 15,
        minWave: 2,          // WAVE 2から出現（硬い敵）
        color: [255, 150, 50],
        rotSpeed: 0.02
    },
    fast: {
        size: 24,
        hp: 100,             // HP
        speedMult: 1.7,
        score: 120,
        spawnWeight: 15,
        minWave: 3,          // WAVE 3から出現（素早い敵）
        color: [150, 50, 255],
        rotSpeed: 0.1
    },
    armored: {
        size: 36,
        hp: 200,             // HP
        speedMult: 0.85,
        score: 200,
        spawnWeight: 13,
        minWave: 4,          // WAVE 4から出現（弾を弾く敵）
        color: [180, 180, 200],
        rotSpeed: 0.03,
        armorChance: 0.35,   // 弾を弾く確率
        dropBonus: 0.3       // ドロップ率ボーナス
    },
    speedy: {
        size: 26,
        hp: 100,             // HP
        speedMult: 2.0,
        score: 150,
        spawnWeight: 9,
        minWave: 5,          // WAVE 5から出現（ジグザグ移動）
        color: [0, 255, 200],
        rotSpeed: 0.15,
        zigzagInterval: 25   // ジグザグ切り替え間隔（少し短く）
    },
    laser: {
        size: 32,
        hp: 200,             // HP
        speedMult: 0.6,      // ゆっくり降りてくる
        score: 250,
        spawnWeight: 8,
        minWave: 6,          // WAVE 6から出現
        color: [255, 0, 100],
        rotSpeed: 0.01,
        stopY: 120,          // 停止するY座標
        chargeTime: 90,      // チャージ時間（1.5秒）
        laserDuration: 60,   // レーザー発射時間（1秒）
        laserWidth: 20,      // レーザーの幅
        warningTime: 60      // 警告表示時間（チャージ中に表示）
    },
    mirror: {
        size: 30,
        hp: 200,             // HP
        speedMult: 0.3,      // 非常に遅い
        score: 200,
        spawnWeight: 10,
        minWave: 5,          // WAVE 5から出現
        color: [150, 200, 255],
        rotSpeed: 0.02,
        laneChangeDelay: 60  // レーン変更の待機時間（フレーム）※1秒
    },
    boss: {
        // --- 基本ステータス ---
        size: 65,
        baseHp: 1200,        // HP
        hpPerWave: 400,      // WAVE毎のHP増加
        speedMult: 0.45,
        score: 1500,
        color: [255, 0, 0],
        innerColor: [255, 100, 0],
        rotSpeed: 0.02,
        laneChangeInterval: 60,
        // --- 出現設定 ---
        spawnWave: 5,            // ボスが出現するウェーブ間隔
        warningDuration: 90,     // ボス警告の表示時間（フレーム）
        defeatPauseDuration: 120, // ボス撃破後の敵出現停止時間（フレーム）
        // --- 滞在フェーズ設定 ---
        stayDuration: 360,       // ボスが上部に留まる時間（フレーム）※6秒
        stayPositionY: 180,      // ボスが留まるY座標
        // --- 下降フェーズ設定 ---
        descentSpeed: 1.5,       // 下降速度
        descentDistance: 60,     // 1回の下降距離
        descentPauseDuration: 60, // 下降後の停止時間（フレーム）
        zigzagAmplitude: 80,     // ジグザグの振幅（左右の動き幅）
        zigzagSpeed: 0.04,       // ジグザグの速度（周期）
        playerTrackRate: 0.008,  // プレイヤー追跡率（非常に緩やか）
        // --- 攻撃パターン ---
        attack: {
            straightInterval: 50,
            spreadInterval: 80,
            spreadCount: 5,
            homingInterval: 100,
            barrageInterval: 25,
            barrageCount: 8
        }
    }
} as const;
